import React, { useState } from 'react';
import { Pencil, Trash2, AlertCircle, Info } from 'lucide-react';
import { Shortcut, UserPreferences } from '../types';
import './ShortcutTable.css';

interface ShortcutTableProps {
  shortcuts: Shortcut[];
  preferences: UserPreferences;
  currentUserId: string;
  currentUserEmail?: string;
  onToggle: (shortcutId: string) => void;
  onEdit: (shortcut: Shortcut) => void;
  onDelete: (shortcut: Shortcut) => void;
  onUpdateCustomExpansion: (shortcutId: string, expansion: string) => void;
}

export function ShortcutTable({
  shortcuts,
  preferences,
  currentUserId,
  currentUserEmail,
  onToggle,
  onEdit,
  onDelete,
  onUpdateCustomExpansion
}: ShortcutTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'disabled'>('all');

  const filteredShortcuts = shortcuts.filter(shortcut => {
    const matchesSearch = searchQuery === '' || 
      shortcut.shortcut.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shortcut.expansion.toLowerCase().includes(searchQuery.toLowerCase());
    
    const isDisabled = preferences.disabledShortcuts.includes(shortcut.id);
    const matchesFilter = filter === 'all' || 
      (filter === 'active' && !isDisabled) ||
      (filter === 'disabled' && isDisabled);
    
    return matchesSearch && matchesFilter;
  });

  const regularShortcuts = filteredShortcuts.filter(s => !s.isTemplate);
  const templateShortcuts = filteredShortcuts.filter(s => s.isTemplate);

  const renderShortcutRow = (shortcut: Shortcut) => {
    const isDisabled = preferences.disabledShortcuts.includes(shortcut.id);
    const customExpansion = preferences.customExpansions[shortcut.id];
    const displayExpansion = shortcut.isTemplate && customExpansion ? customExpansion : shortcut.expansion;
    // Check if user can edit - createdBy might be email or userId
    const canEdit = shortcut.createdBy === currentUserId || 
                   shortcut.createdBy === currentUserEmail ||
                   currentUserEmail?.endsWith('@gangerdermatology.com');
    const canDelete = shortcut.createdBy === currentUserId || 
                     shortcut.createdBy === currentUserEmail ||
                     currentUserEmail?.endsWith('@gangerdermatology.com');

    return (
      <tr key={shortcut.id} className={isDisabled ? 'disabled' : ''}>
        <td className="shortcut-cell">{shortcut.shortcut}</td>
        <td className="expansion-cell">
          {shortcut.isTemplate ? (
            <input
              type="text"
              value={customExpansion || ''}
              placeholder={shortcut.expansion}
              onChange={(e) => onUpdateCustomExpansion(shortcut.id, e.target.value)}
              className="custom-expansion-input"
            />
          ) : (
            displayExpansion
          )}
        </td>
        <td className="toggle-cell">
          <label className="toggle-switch" aria-label={`Toggle ${shortcut.shortcut} shortcut`}>
            <input
              type="checkbox"
              checked={!isDisabled}
              onChange={() => onToggle(shortcut.id)}
              aria-checked={!isDisabled}
              role="switch"
            />
            <span className="toggle-slider" aria-hidden="true"></span>
          </label>
        </td>
        <td className="actions-cell">
          {canEdit && !shortcut.isTemplate && (
            <button
              className="icon-btn"
              onClick={() => onEdit(shortcut)}
              title="Edit"
            >
              <Pencil size={16} />
            </button>
          )}
          {canDelete && (
            <button
              className="icon-btn danger"
              onClick={() => onDelete(shortcut)}
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="shortcut-table-container">
      <div className="table-controls">
        <input
          type="text"
          placeholder="Search shortcuts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`filter-btn ${filter === 'disabled' ? 'active' : ''}`}
            onClick={() => setFilter('disabled')}
          >
            Disabled
          </button>
        </div>
      </div>

      {regularShortcuts.length > 0 && (
        <div className="table-section">
          <h3>Shared Shortcuts ({regularShortcuts.length})</h3>
          <table className="shortcut-table">
            <thead>
              <tr>
                <th>Shortcut</th>
                <th>Expands To</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {regularShortcuts.map(renderShortcutRow)}
            </tbody>
          </table>
        </div>
      )}

      {templateShortcuts.length > 0 && (
        <div className="table-section">
          <h3>User-Specific Templates ({templateShortcuts.length})</h3>
          <table className="shortcut-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Your Value</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templateShortcuts.map(renderShortcutRow)}
            </tbody>
          </table>
        </div>
      )}

      {filteredShortcuts.length === 0 && (
        <div className="empty-state">
          {searchQuery ? (
            <>
              <Info size={48} />
              <p>No shortcuts found matching "{searchQuery}"</p>
            </>
          ) : (
            <>
              <AlertCircle size={48} />
              <p>No shortcuts available</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}